import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { movies } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find(m => m.id === Number(id));

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <SEO
          title="影片未找到 - 草民影院"
          description="抱歉，您要找的影片不存在或已被移除。请浏览草民影院其他精彩影视内容。"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main>
            <div className="pt-16 md:pt-20 px-4 flex flex-col items-center justify-center min-h-[60vh]">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 mb-4">
                <i className="ri-error-warning-line text-foreground-400 text-3xl"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-900 mb-2">影片未找到</h1>
              <p className="text-foreground-500 mb-6">您要找的影片不存在或已被移除</p>
              <Link
                to="/"
                className="px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedMovies = movies
    .filter(m => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${movie.title} - 免费在线观看 | 草民影院`}
        description={`${movie.title}：${movie.description.slice(0, 120)}... 草民影院提供高清不卡在线观看，主演${movie.actors.slice(0, 3).join('、')}等。`}
        keywords={`${movie.title},免费${movie.category},在线观看,${movie.actors.slice(0, 3).join(',')},${movie.region},草民影院`}
        canonicalUrl={`https://caominyingyuan.com/article/${movie.id}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-16 md:pt-20">
            {/* Breadcrumb */}
            <div className="w-full px-4 md:px-8 bg-white">
              <div className="max-w-screen-2xl mx-auto">
                <Breadcrumb />
              </div>
            </div>

            {/* Movie Detail */}
            <section className="w-full px-4 md:px-8 py-8 bg-white">
              <div className="max-w-screen-2xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Poster */}
                  <div className="flex-shrink-0 mx-auto lg:mx-0">
                    <div className="w-56 md:w-64 h-80 md:h-96 rounded-xl overflow-hidden">
                      <img
                        src={movie.coverImage}
                        alt={`${movie.title}海报 - 草民影院免费在线观看`}
                        title={`${movie.title} - 草民影院免费${movie.category}在线观看`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-serif">
                      {movie.title}
                    </h1>

                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-bold">
                        {movie.rating} 分
                      </span>
                      <span className="text-sm text-foreground-500">{movie.year}</span>
                      <span className="text-sm text-foreground-500">·</span>
                      <span className="text-sm text-foreground-500">{movie.region}</span>
                      <span className="text-sm text-foreground-500">·</span>
                      <span className="text-sm text-foreground-500">{movie.duration}</span>
                      {(movie.isNew || movie.isHot) && (
                        <>
                          {movie.isNew && (
                            <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">NEW</span>
                          )}
                          {movie.isHot && (
                            <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">热播</span>
                          )}
                        </>
                      )}
                    </div>

                    <div className="mt-4">
                      <span className="text-sm text-foreground-500">类型：</span>
                      <span className="text-sm text-foreground-900">{movie.type}</span>
                    </div>

                    <div className="mt-2">
                      <span className="text-sm text-foreground-500">导演：</span>
                      <span className="text-sm text-foreground-900">{movie.director}</span>
                    </div>

                    <div className="mt-2">
                      <span className="text-sm text-foreground-500">主演：</span>
                      <span className="text-sm text-foreground-900">
                        {movie.actors.join(' / ')}
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-sm text-foreground-500">标签：</span>
                      {movie.tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-0.5 rounded-full bg-background-100 text-xs text-foreground-600">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Play Button */}
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <button className="px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/30">
                        <i className="ri-play-circle-fill mr-2"></i>
                        立即播放
                      </button>
                      <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-500 hover:border-primary-200 transition-colors cursor-pointer">
                        <i className="ri-heart-line text-lg"></i>
                      </button>
                      <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-500 hover:border-primary-200 transition-colors cursor-pointer">
                        <i className="ri-share-forward-line text-lg"></i>
                      </button>
                    </div>

                    {/* Description */}
                    <div className="mt-6">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">剧情简介</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {movie.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Player Placeholder */}
            <section className="w-full px-4 md:px-8 py-8 bg-background-900">
              <div className="max-w-screen-2xl mx-auto">
                <div className="aspect-video max-w-4xl mx-auto rounded-xl bg-background-800 flex items-center justify-center relative overflow-hidden">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-primary-500/20 mb-4 cursor-pointer hover:bg-primary-500/30 transition-colors">
                      <i className="ri-play-fill text-white text-3xl ml-1"></i>
                    </div>
                    <p className="text-white/80 text-sm">点击播放 {movie.title}</p>
                    <p className="text-foreground-500 text-xs mt-1">{movie.updateEpisode} · 高清画质</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Videos */}
            <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
              <div className="max-w-screen-2xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif">
                      猜你喜欢
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">根据该影片推荐的相关内容</p>
                  </div>
                </div>

                {relatedMovies.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                    {relatedMovies.map((m) => (
                      <MovieCard key={m.id} movie={m} size="normal" />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <p className="text-foreground-500 text-sm">暂无更多推荐</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://caominyingyuan.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: movie.category,
                item: `https://caominyingyuan.com/category/${movie.categorySlug}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: movie.title,
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${movie.title} - 草民影院免费在线观看`,
            description: movie.description,
            author: {
              '@type': 'Organization',
              name: '草民影院',
            },
            datePublished: `${movie.year}-01-01`,
            image: movie.coverImage,
          }),
        }}
      />
    </>
  );
}